import React, { useState, useEffect } from 'react';
import { db } from '../utils/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { MagazineContent } from '../types';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight } from 'lucide-react';

const MagazinePage: React.FC = () => {
  const [contents, setContents] = useState<MagazineContent[]>([]);
  const [filteredContents, setFilteredContents] = useState<MagazineContent[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'entries' | 'lookbook'>('all');
  const [loading, setLoading] = useState(true);

  // Header — CMS-overridable via page_content/archive.
  // Default is the plain text header; an image renders as a dark banner behind it.
  const [pageContent, setPageContent] = useState({
    title: 'Archive',
    subtitle: '',
    heroImage: ''
  });

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, 'page_content', 'archive'));
        if (snap.exists()) {
          const data = snap.data();
          setPageContent(prev => ({
            title: data.title || prev.title,
            subtitle: data.subtitle || '',
            heroImage: data.heroImage || ''
          }));
        }
      } catch {
        // Keep the plain header silently
      }
    };
    fetchPageContent();
  }, []);

  useEffect(() => {
    const fetchMagazineContent = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'magazine'),
          where('visible', '==', true)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const allContents = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as MagazineContent[];

          // Client-side sort by createdAt (newest first)
          // Handling potential missing or different timestamp formats safely
          allContents.sort((a, b) => {
            const getMillis = (t: any) => {
              if (!t) return 0;
              if (t.toMillis) return t.toMillis(); // Firestore Timestamp
              if (t instanceof Date) return t.getTime(); // JS Date
              if (t.seconds) return t.seconds * 1000; // Raw object
              return 0;
            };
            return getMillis(b.createdAt) - getMillis(a.createdAt);
          });

          setContents(allContents);
          setFilteredContents(allContents);
        }
      } catch (error) {
        console.error("Error fetching magazine content:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMagazineContent();
  }, []);

  useEffect(() => {
    if (selectedFilter === 'all') {
      setFilteredContents(contents);
    } else if (selectedFilter === 'lookbook') {
      setFilteredContents(contents.filter(content => content.type === 'lookbook'));
    } else {
      // Entries = everything written that isn't a lookbook
      setFilteredContents(contents.filter(content => content.type !== 'lookbook'));
    }
  }, [selectedFilter, contents]);

  const featuredContent = contents.find(c => c.featured);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'lookbook': return 'Lookbook';
      case 'editorial': return 'Editorial';
      case 'campaign': return 'Campaign';
      case 'runway': return 'Runway';
      default: return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'lookbook': return 'bg-blue-600';
      case 'editorial': return 'bg-purple-600';
      case 'campaign': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header — plain by default; dark image banner when the CMS provides one */}
      {pageContent.heroImage ? (
        <div className="relative mt-20 h-[200px] md:h-[250px] bg-gray-900 overflow-hidden mb-4">
          <img
            src={pageContent.heroImage}
            alt=""
            aria-hidden="true"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
            <h1 className="text-4xl md:text-5xl font-display font-normal uppercase tracking-wider">
              {pageContent.title}
            </h1>
            {pageContent.subtitle && (
              <p className="mt-3 font-mono text-xs md:text-sm tracking-widest text-white/80">{pageContent.subtitle}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="pt-32 pb-12 px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-display font-normal uppercase tracking-wider mb-8">
            {pageContent.title}
          </h1>
        </div>
      )}

      {/* Filter Tabs - Minimal Style */}
      <div className="container mx-auto px-4 pb-8 border-b border-gray-200">
        <div className="flex justify-center gap-8 flex-wrap text-sm">
          {([
            { key: 'all', label: 'All' },
            { key: 'entries', label: 'Entries' },
            { key: 'lookbook', label: 'Lookbooks' },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setSelectedFilter(tab.key)}
              className={`font-heading uppercase tracking-wider transition-colors pb-2 ${
                selectedFilter === tab.key
                  ? 'text-black border-b-2 border-black font-bold'
                  : 'text-gray-500 hover:text-black'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Vertical Feed - Single Column */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {filteredContents.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 font-mono text-sm tracking-wider">The archive is being assembled.</p>
          </div>
        ) : (
          <div className="space-y-20">
            {filteredContents.map((content) => (
              <article key={content.id} className="border-b border-gray-200 pb-20 last:border-b-0">
                <Link to={`/magazine/${content.id}`} className="group block">
                  {/* Title */}
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4 group-hover:text-gray-600 transition-colors">
                    {content.title}
                  </h2>

                  {/* Metadata */}
                  <div className="mb-4 text-xs text-gray-500 uppercase tracking-wider flex items-center gap-4 flex-wrap">
                    <span className="text-black font-bold">{getTypeLabel(content.type)}</span>
                    {content.season && <span>{content.season}</span>}
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(content.publishDate)}
                    </span>
                    {content.author && (
                      <span className="flex items-center gap-1">
                        <User size={12} />
                        {content.author}
                      </span>
                    )}
                  </div>

                  {/* Featured Image */}
                  <div className="w-full mb-8 overflow-hidden bg-gray-100">
                    <img
                      src={content.coverImage || '/assets/placeholder.svg'}
                      alt={content.title}
                      onError={(e) => {
                        const t = e.currentTarget;
                        if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                      }}
                      className="w-full h-auto object-cover transition-opacity duration-300 group-hover:opacity-90"
                    />
                  </div>

                  {/* Subtitle */}
                  {content.subtitle && (
                    <p className="text-lg font-body text-gray-700 mb-4">{content.subtitle}</p>
                  )}

                  {/* Description Excerpt */}
                  <p className="font-body text-gray-600 leading-relaxed mb-6">
                    {content.description}
                  </p>

                  {/* Continue Reading Link */}
                  <span className="inline-flex items-center gap-2 text-sm font-heading uppercase tracking-wider text-black hover:text-gray-600 transition-colors">
                    Continue Reading <ArrowRight size={16} />
                  </span>
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MagazinePage;
